import React from 'react';
import { Eye, EyeOff, HeadphonesIcon } from 'lucide-react';
import api from '../api';
import SupportModal from './SupportModal';

const Login = ({ setAuth }) => {
  const [mode, setMode] = React.useState('login'); // 'login' or 'register'
  const [email, setEmail] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [showSupport, setShowSupport] = React.useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      // Enviamos 'email' en lugar de 'username' para que coincida con tu nuevo modelo
      const res = await api.post('token/', { email, password });
      
      sessionStorage.setItem('token', res.data.access);
      sessionStorage.setItem('refresh_token', res.data.refresh);

      if (res.data.must_change_password) {
        sessionStorage.setItem('must_change', 'true');
      } else {
        sessionStorage.removeItem('must_change');
      }
      
      const role = res.data.user?.role || '';
      sessionStorage.setItem('userRole', role);
      sessionStorage.setItem('userEmail', res.data.user?.email || '');
      const firstName = res.data.user?.first_name || '';
      const lastName = res.data.user?.last_name || '';
      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      if (fullName) sessionStorage.setItem('userName', fullName);
      
      // Guardamos las diplomaturas coordinadas para filtrar menús
      const coordinatedDiplos = res.data.user?.profile?.coordinated_diplos || [];
      sessionStorage.setItem('userCoordinatedDiplos', JSON.stringify(coordinatedDiplos.map(id => id.toString())));
      
      setAuth(true);
    } catch (err) {
      let errorMsg = err.response?.data?.detail || 'Error de credenciales o cuenta no aprobada';
      if (typeof err.response?.data === 'string' && err.response.data.includes('<!doctype html>')) {
        errorMsg = 'Error del servidor (500). Por favor, intenta más tarde.';
      }
      setMessage(errorMsg);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setMessage('');

    if (password !== confirmPassword) {
      setMessage('Las contraseñas no coinciden');
      return;
    }

    try {
      // dj-rest-auth usa password1 y password2 para el registro
      await api.post('auth/registration/', { 
        username: email, 
        email, 
        password1: password,
        password2: confirmPassword
      });
      setMessage('Registro exitoso. Ya puedes iniciar sesión con tu nueva cuenta.');
      setMode('login');
    } catch (err) {
      const errorData = err.response?.data;
      let detail = 'Error en el registro';
      if (typeof errorData === 'string') {
        detail = errorData;
      } else if (errorData && typeof errorData === 'object') {
        // Formatear errores comunes de Django/allauth
        detail = Object.entries(errorData)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
          .join(' | ');
      }
      setMessage('Error en el registro: ' + detail);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setMessage('Por favor, ingresa tu correo primero en el campo de arriba.');
      return;
    }
    try {
      // Pegamos al endpoint de dj-rest-auth
      await api.post('auth/password/reset/', { email });
      setMessage('Si el correo existe, se envió un link de recuperación. Revisa tu email (o la consola del backend).');
    } catch (err) {
      let errorMsg = err.response?.data?.email?.[0] || err.response?.data?.detail || 'Error al solicitar recuperación. Por favor, intenta de nuevo.';
      if (typeof err.response?.data === 'string' && err.response.data.includes('<!doctype html>')) {
        errorMsg = 'Error del servidor (500). Por favor, intenta más tarde.';
      }
      setMessage(errorMsg);
    }
  };

  const handleMagicLogin = async () => {
    try {
      const res = await api.post('users/magic_link_login/', { email });
      sessionStorage.setItem('token', res.data.access);
      if (res.data.refresh) sessionStorage.setItem('refresh_token', res.data.refresh);
      setAuth(true);
    } catch (err) {
      setMessage('No se encontró cuenta verificada para este correo');
    }
  };

  return (
    <div className="login-page">
      <div className="login-overlay"></div>
      <div className="login-content-container animate-fade-in">
        <div className="login-card glassmorphism p-5 shadow-2xl">
          <div className="text-center mb-5">
            <h1 className="brand-title">DIPLOMATURA<span>Puentes</span></h1>
            <p className="text-muted">Gestión Integral de Diplomaturas</p>
          </div>
          
          <div className="mode-toggle mb-4 d-flex justify-content-center gap-3">
            <button 
              className={`btn ${mode === 'login' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setMode('login')}
            >
              Iniciar Sesión
            </button>
            <button 
              className={`btn ${mode === 'register' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setMode('register')}
            >
              Registrarse
            </button>
          </div>

          <form onSubmit={mode === 'login' ? handleLogin : handleRegister}>
            <div className="form-floating mb-3">
              <input 
                type="email" 
                className="form-control" 
                id="email" 
                placeholder="nombre@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <label htmlFor="email">Correo Electrónico</label>
            </div>

            {mode === 'register' && (
              <>
                {/* Usuario ahora se deriva del email automáticamente */}
              </>
            )}

            <div className="form-floating mb-3 position-relative">
              <input 
                type={showPassword ? "text" : "password"} 
                className="form-control pe-5" 
                id="password" 
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <label htmlFor="password">Contraseña</label>
              <button
                type="button"
                className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent text-muted me-2"
                style={{ zIndex: 10 }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {mode === 'register' && (
              <div className="form-floating mb-4 position-relative">
                <input 
                  type={showConfirmPassword ? "text" : "password"} 
                  className="form-control pe-5" 
                  id="confirmPassword" 
                  placeholder="Confirmar Contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <label htmlFor="confirmPassword">Confirmar Contraseña</label>
                <button
                  type="button"
                  className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent text-muted me-2"
                  style={{ zIndex: 10 }}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            )}

            {mode === 'login' && (
              <div className="text-end mb-3">
                <button 
                  type="button" 
                  onClick={handleForgotPassword} 
                  className="btn btn-link p-0 text-decoration-none text-sm"
                  style={{ color: '#0d6efd', fontSize: '0.85rem' }}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div> 
            )}

            <button type="submit" className="btn btn-primary-gradient w-100 mb-3 py-3 font-bold">
              {mode === 'login' ? 'INICIAR SESIÓN' : 'REGISTRARSE'}
            </button>

          </form>
          
          {/* Support button */}
          <div className="text-center mt-4">
            <button
              type="button"
              className="btn btn-warning fw-bold px-4 py-2 d-flex align-items-center gap-2 mx-auto"
              style={{ borderRadius: '2rem' }}
              onClick={() => setShowSupport(true)}
            >
              <HeadphonesIcon size={18} /> SOPORTE TÉCNICO
            </button>
          </div>

          {message && (
            <div className="central-message-overlay">
              <div className="central-message-box">
                <div className={`alert ${message.includes('exitoso') || message.includes('envió') ? 'text-success' : 'text-danger'} mb-4`}>
                  {message}
                </div>
                <button
                  type="button"
                  className="btn-close-message"
                  onClick={() => setMessage('')}
                >
                  ENTENDIDO
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}
    </div>
  );
};

export default Login;
